/**
 * Item Library Integration for Plot Service
 * Maps field names to rates from Item Library API
 */

import { ItemLibraryResponse, ItemLibraryItem } from '../api/itemLibrary';
import { PLOT_SERVICE_REFERENCES } from './plotServiceReferenceMapping';
import { PLOT_SERVICE_FIELD_LABELS, PLOT_SERVICE_FIELD_UNITS } from './plotServiceFieldMapping';

export interface RateInfo {
  rate: number;
  description: string;
  unit: string;
  resourceCode?: string;
}

/**
 * Get rate from item library by resource code
 */
function getRateByCode(
  resourceCode: string,
  resourceCodeMap: Map<string, ItemLibraryItem>,
  fieldName: string,
  fallbackDescription?: string,
): RateInfo {
  const normalizedCode = resourceCode.trim().toUpperCase();
  const item = resourceCodeMap.get(normalizedCode);

  if (item) {
    return {
      rate: item.nettRate || item.baseRate || 0,
      description: item.resourceDescription || fallbackDescription || fieldName,
      unit: fieldName in PLOT_SERVICE_FIELD_UNITS ? PLOT_SERVICE_FIELD_UNITS[fieldName] : (item.unit || ''),
      resourceCode: normalizedCode,
    };
  }

  // Item not found — preserve the expected code so the bill summary can still
  // surface it in the dev-mode badge (matches the Foundations integration).
  // Without this, fields whose codes are missing from the loaded item library
  // (e.g. GWALPDS006 for `plots` / `apartments`) lose their resource-code
  // display entirely.
  return {
    rate: 0,
    description: fallbackDescription || fieldName,
    unit: PLOT_SERVICE_FIELD_UNITS[fieldName] ?? '',
    resourceCode: normalizedCode,
  };
}

/**
 * Create a rate lookup map from item library for Plot Service
 * @param itemLibraryData - Item library response from API
 */
export function createPlotServiceRateLookupMap(
  itemLibraryData: ItemLibraryResponse | undefined,
): Map<string, RateInfo> {
  const rateMap = new Map<string, RateInfo>();

  if (!itemLibraryData) {
    return rateMap;
  }

  // Create a map of resourceCode to item for quick lookup
  const resourceCodeMap = new Map<string, ItemLibraryItem>();
  itemLibraryData.forEach((item: ItemLibraryItem) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) {
      resourceCodeMap.set(code, item);
    }
  });

  // Map each field name to its rate info (rate, description, unit)
  Object.entries(PLOT_SERVICE_REFERENCES).forEach(([fieldName, reference]) => {
    // Get proper label from field mapping as fallback
    const fallbackDescription = PLOT_SERVICE_FIELD_LABELS[fieldName] || fieldName;

    if (!reference.code) {
      // Fields without codes
      rateMap.set(fieldName, {
        rate: 0,
        description: fallbackDescription,
        unit: fieldName in PLOT_SERVICE_FIELD_UNITS ? PLOT_SERVICE_FIELD_UNITS[fieldName] : '',
      });
      return;
    }

    // Static code from reference mapping - pass fallback description
    const rateInfo = getRateByCode(reference.code, resourceCodeMap, fieldName, fallbackDescription);
    rateMap.set(fieldName, {
      ...rateInfo,
      unit: fieldName in PLOT_SERVICE_FIELD_UNITS ? PLOT_SERVICE_FIELD_UNITS[fieldName] : rateInfo.unit,
    });
  });

  return rateMap;
}

/**
 * Get rate info for a specific field name
 */
export function getPlotServiceRateInfo(
  fieldName: string,
  rateLookupMap: Map<string, RateInfo>,
): RateInfo {
  return rateLookupMap.get(fieldName) || { rate: 0, description: fieldName, unit: PLOT_SERVICE_FIELD_UNITS[fieldName] ?? '' };
}

/**
 * Apply rates and descriptions to Plot Service summary items
 */
export function applyPlotServiceRatesToSummary(
  summaryItems: any[],
  rateLookupMap: Map<string, RateInfo>,
): any[] {
  return summaryItems.map((item) => {
    const rateInfo = getPlotServiceRateInfo(item.fieldName, rateLookupMap);
    const amount = Math.abs(item.quantity * rateInfo.rate);

    return {
      ...item,
      rate: rateInfo.rate,
      description: item.description,
      unit: rateInfo.unit,
      amount,
      resourceCode: rateInfo.resourceCode,
    };
  });
}
